// Row 122 RecruiterScreen (`screen`, DC-220 … DC-222): the Step 8 card on the Recruiter Screen round page. Header: the
// candidate tile, name, Candidate ID (IdPill), the requisition id in mono and the status pill. G5's Rail of his chain
// *Screen Requested · Scheduled · Completed · outcome* (the last step takes the outcome word; Hold is a warn step, not
// terminal). The dates *First contact · Screen · Follow-up* and *Screen content*. The outcome is G2's four-tile
// RecoTiles *Advance · Hold · Pass · Withdrawn*; the chosen tile discloses its fields (Hold / Pass: the reason; Withdrawn:
// G1's ReasonBox *Withdrawal reason* with the stage and the date). Until *Completed*, Advance · Hold · Pass are dashed
// and described by his chain sentence while Withdrawn stays open. **Record outcome** is the one primary, dashed until a
// tile and its required fields are set; an outcome on record shows G3's Stamp.
// Who sees it (BRD §3.8): the Recruiter edits and records; the Coordinator sees it read-only (values, no controls); the
// HM sees only the outcome pill and its date once there is one, and nothing before; the Interviewer sees nothing.
// States: loading draws the card at its final height; empty is his sentence for a candidate with no application;
// error is the scheduling section's Sync strip; refused is the red banner with his rule, the entered values kept.

import { useState, type ReactNode } from "react";
import {
  Avatar, Button, Card, Field, IdPill, Input, KeyValue, Pill, Select, SURFACE_LABELS, TextArea, type Blocked, type SelectOption,
} from "../core";
import { ACTION_LABELS, RULE_LABELS, SCREEN_STATUS, type Tone } from "../labels";
import { cx } from "../lib/cx";
import { formatDate, toEpoch, type Instant } from "../lib/time";
import { G1_LABELS, ReasonBox } from "../g1";
import { RecoTiles, type RecoOption } from "../g2";
import { Stamp } from "../g3";
import { Rail, Sync, type RailStep, type SyncProps } from "../g5";
import { SCREEN_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export type ScreenStatus = "screenRequested" | "scheduled" | "completed" | "advance" | "hold" | "pass" | "withdrawn";
export type ScreenSeat = "recruiter" | "coordinator" | "hm" | "interviewer";
export type ScreenOutcome = "advance" | "hold" | "pass" | "withdrawn";

export const SCREEN_STATUSES: readonly ScreenStatus[] = ["screenRequested", "scheduled", "completed", "advance", "hold", "pass", "withdrawn"];
export const SCREEN_SEATS: readonly ScreenSeat[] = ["recruiter", "coordinator", "hm", "interviewer"];

/** The status pill of each status (DS row 122 table). */
export const SCREEN_PILL: Readonly<Record<ScreenStatus, { word: string; tone: Tone }>> = {
  screenRequested: { word: SCREEN_STATUS.screenRequested, tone: "info" },
  scheduled: { word: SCREEN_STATUS.scheduled, tone: "ok" },
  completed: { word: SCREEN_STATUS.completed, tone: "neutral" },
  advance: { word: SCREEN_STATUS.advance, tone: "ok" },
  hold: { word: SCREEN_STATUS.hold, tone: "warn" },
  pass: { word: SCREEN_STATUS.pass, tone: "neutral" },
  withdrawn: { word: SCREEN_STATUS.withdrawn, tone: "neutral" },
};

const OUTCOMES: readonly ScreenOutcome[] = ["advance", "hold", "pass", "withdrawn"];
const isOutcome = (status: ScreenStatus): status is ScreenOutcome => (OUTCOMES as readonly string[]).includes(status);
const TERMINAL: readonly ScreenStatus[] = ["advance", "pass", "withdrawn"];

/** The outcome being chosen, or the one on record. Dates are yyyy-mm-dd. */
export interface ScreenOutcomeDraft {
  value?: ScreenOutcome | undefined;
  /** Hold / Pass: the reason's value; Withdrawn: the withdrawal reason's value. */
  reason?: string | undefined;
  stage?: string | undefined;
  date?: string | undefined;
}

export interface ScreenDates {
  firstContact?: string | undefined;
  screen?: string | undefined;
  followUp?: string | undefined;
}

export interface RecruiterScreenProps {
  status: ScreenStatus;
  /** Whose view (default recruiter). */
  seat?: ScreenSeat | undefined;
  candidate: { name: string; id: string; hueKey?: string | undefined };
  /** The application's requisition id (mono). */
  requisition: string;
  dates?: ScreenDates | undefined;
  onDate?: ((key: keyof ScreenDates, value: string) => void) | undefined;
  content?: string | undefined;
  onContent?: ((value: string) => void) | undefined;
  /** Hold and Pass reasons (the Admin's configuration). */
  reasons: readonly SelectOption[];
  /** Withdrawal reasons (the Admin's configuration). */
  withdrawalReasons: readonly SelectOption[];
  /** The stages a withdrawal may be recorded at (the first is the default, *Recruiter screen*). */
  stages: readonly SelectOption[];
  /** The outcome on record (Hold, or a terminal outcome), or a draft to start from. */
  outcome?: ScreenOutcomeDraft | undefined;
  onOutcome?: ((draft: ScreenOutcomeDraft) => void) | undefined;
  /** Who recorded the outcome on record, and when. */
  recorded?: { actor: string; at: Instant } | undefined;
  onRecord?: ((draft: ScreenOutcomeDraft) => void) | undefined;
  /** The record request in flight. */
  recording?: boolean | undefined;
  loading?: boolean | undefined;
  /** No application exists for this candidate: his sentence instead of the card. */
  noApplication?: boolean | undefined;
  /** The scheduling section's integration strip (the Er state). */
  sync?: SyncProps | undefined;
  /** The server refused the record: his rule on the red banner; the entered values stay. */
  refused?: { reason: string } | undefined;
  timeZone?: string | undefined;
  /** The scheduling section the same page draws under the card. */
  children?: ReactNode;
  hs?: {
    firstContact?: Hs | undefined; screen?: Hs | undefined; followUp?: Hs | undefined; content?: Hs | undefined;
    tiles?: Partial<Record<ScreenOutcome, Hs>> | undefined; reason?: Hs | undefined; stage?: Hs | undefined; date?: Hs | undefined;
    record?: Hs | undefined; id?: Hs | undefined;
  } | undefined;
  className?: string | undefined;
}

function railSteps(status: ScreenStatus): RailStep[] {
  const at = status === "screenRequested" ? 0 : status === "scheduled" ? 1 : status === "completed" ? 2 : 3;
  const chain = [SCREEN_STATUS.screenRequested, SCREEN_STATUS.scheduled, SCREEN_STATUS.completed];
  const steps: RailStep[] = chain.map((label, index) => ({ label, state: index < at ? "done" : index === at ? "on" : "next" }));
  const last = isOutcome(status) ? SCREEN_PILL[status].word : SURFACE_LABELS.outcome;
  steps.push({ label: last, state: status === "hold" ? "warn" : isOutcome(status) ? "done" : "next" });
  return steps;
}

/** Why Record outcome is not yet possible, or null when it is. */
function recordReason(draft: ScreenOutcomeDraft): string | null {
  if (!draft.value) return SCREEN_LABELS.chain;
  if (draft.value === "hold" || draft.value === "pass") return draft.reason ? null : G1_LABELS.requireReason;
  if (draft.value === "withdrawn") return draft.reason && draft.stage && draft.date ? null : RULE_LABELS.withdrawalRequires;
  return null;
}

const label = (options: readonly SelectOption[], value: string | undefined) => options.find((option) => option.value === value)?.label ?? "—";

function Header({ candidate, requisition, idHs }: Pick<RecruiterScreenProps, "candidate" | "requisition"> & { idHs: Hs | undefined }) {
  return (
    <div className="mc-scr-who">
      <Avatar name={candidate.name} hueKey={candidate.hueKey ?? candidate.id} candidate size={44} decorative />
      <b className="mc-scr-name">{candidate.name}</b>
      <IdPill value={candidate.id} hs={idHs} />
      <span className="mc-mono mc-scr-req">{requisition}</span>
    </div>
  );
}

export function RecruiterScreen(props: RecruiterScreenProps) {
  const {
    status, seat = "recruiter", candidate, requisition, dates = {}, onDate, content, onContent, reasons, withdrawalReasons, stages, outcome,
    onOutcome, recorded, onRecord, recording, loading, noApplication, sync, refused, timeZone, children, hs = {}, className,
  } = props;
  const [own, setOwn] = useState<ScreenOutcomeDraft>(() => ({
    stage: stages[0]?.value, ...(outcome ?? {}), ...(outcome?.value === undefined && isOutcome(status) ? { value: status } : {}),
  }));
  const draft = own;
  const change = (next: ScreenOutcomeDraft) => { setOwn(next); onOutcome?.(next); };

  if (seat === "interviewer") return null;
  const decided = isOutcome(status);
  if (seat === "hm" && !decided) return null;
  const pill = SCREEN_PILL[status];
  const statusPill = <Pill tone={pill.tone}>{pill.word}</Pill>;
  const shell = { title: SCREEN_LABELS.recruiterScreen, icon: "phone" as const, className: cx("mc-scr", className) };

  if (noApplication) return <Card {...shell} empty={RULE_LABELS.screenNeedsApplication} />;
  if (loading) return <Card {...shell} loading={{ height: 420 }} />;

  if (seat === "hm") {
    return (
      <Card {...shell} pills={statusPill}>
        <Header candidate={candidate} requisition={requisition} idHs={hs.id} />
        <KeyValue
          cols={1}
          items={[[SURFACE_LABELS.outcome, (
            <span className="mc-scr-hm">
              {statusPill}
              {recorded ? <time dateTime={new Date(toEpoch(recorded.at)).toISOString()}>{formatDate(recorded.at, timeZone)}</time> : null}
            </span>
          )]]}
        />
      </Card>
    );
  }

  const terminal = TERMINAL.includes(status);
  const edit = seat === "recruiter" && !terminal;
  const dateValue = (key: keyof ScreenDates) => dates[key] ?? "";
  const shownDate = (key: keyof ScreenDates) => (dates[key] ? formatDate(`${dates[key]}T16:00:00Z`, timeZone) : "—");
  const dateKeys: [keyof ScreenDates, string, Hs | undefined][] = [
    ["firstContact", SCREEN_LABELS.firstContact, hs.firstContact], ["screen", SCREEN_LABELS.screen, hs.screen], ["followUp", SCREEN_LABELS.followUp, hs.followUp],
  ];

  // The outcome tiles: before Completed only Withdrawn is open; the others are dashed with his chain sentence.
  const early = status === "screenRequested" || status === "scheduled";
  const fields = (value: ScreenOutcome): ReactNode => {
    if (value === "advance") return undefined;
    if (value === "withdrawn") {
      return (
        <div className="mc-scr-fields">
          <ReasonBox
            reasons={withdrawalReasons}
            reason={draft.reason ?? ""}
            onReason={(reason) => change({ ...draft, reason })}
            reasonLabel={G1_LABELS.withdrawalReason}
            notesLabel={false}
            hs={hs.reason !== undefined ? { reason: hs.reason } : {}}
          />
          <Field label={SCREEN_LABELS.withdrawalStage} required>
            <Select options={stages} value={draft.stage ?? ""} onChange={(stage) => change({ ...draft, stage })} hs={hs.stage} />
          </Field>
          <Field label={SCREEN_LABELS.withdrawalDate} required>
            <Input type="date" value={draft.date ?? ""} onChange={(event) => change({ ...draft, date: event.target.value })} hs={hs.date} />
          </Field>
        </div>
      );
    }
    return (
      <Field label={G1_LABELS.reason} required hint={G1_LABELS.requireReason}>
        <Select options={reasons} value={draft.reason ?? ""} onChange={(reason) => change({ ...draft, reason })} hs={hs.reason} />
      </Field>
    );
  };
  const options: RecoOption[] = OUTCOMES.map((value) => ({
    value,
    label: SCREEN_PILL[value].word,
    fields: fields(value),
    blocked: early && value !== "withdrawn" ? { reason: SCREEN_LABELS.chain } : undefined,
    hs: hs.tiles?.[value],
  }));
  const why = recordReason(draft);
  const recordBlocked: Blocked | undefined = why ? { reason: why } : undefined;

  const onRecordClick = () => { if (!why) onRecord?.(draft); };

  // The outcome on record, as values (terminal outcomes; Hold for a Coordinator).
  const recordedValues = (): ReactNode => {
    const value = draft.value;
    if (!value) return null;
    const items: [ReactNode, ReactNode, ReactNode?][] = [[SURFACE_LABELS.outcome, <Pill key="p" tone={SCREEN_PILL[value].tone}>{SCREEN_PILL[value].word}</Pill>, ""]];
    if (value === "hold" || value === "pass") items.push([G1_LABELS.reason, label(reasons, draft.reason), ""]);
    if (value === "withdrawn") {
      items.push(
        [G1_LABELS.withdrawalReason, label(withdrawalReasons, draft.reason), ""],
        [SCREEN_LABELS.withdrawalStage, label(stages, draft.stage), ""],
        [SCREEN_LABELS.withdrawalDate, draft.date ? formatDate(`${draft.date}T16:00:00Z`, timeZone) : "—", ""],
      );
    }
    return <KeyValue cols={2} items={items} className="mc-scr-out-v" />;
  };

  return (
    <Card {...shell} pills={statusPill} refused={refused}>
      <Header candidate={candidate} requisition={requisition} idHs={hs.id} />
      <Rail label={SCREEN_LABELS.recruiterScreen} steps={railSteps(status)} className="mc-scr-rail" />
      {edit ? (
        <div className="mc-scr-dates">
          {dateKeys.map(([key, words, hot]) => (
            <Field key={key} label={words}>
              <Input type="date" value={dateValue(key)} onChange={(event) => onDate?.(key, event.target.value)} hs={hot} />
            </Field>
          ))}
        </div>
      ) : (
        <KeyValue cols={3} items={dateKeys.map(([key, words]) => [words, shownDate(key)] as const)} className="mc-scr-dates-v" />
      )}
      <Field label={SCREEN_LABELS.screenContent} mode={edit ? "edit" : "view"}>
        <TextArea rows={3} value={content ?? ""} onChange={(event) => onContent?.(event.target.value)} hs={hs.content} />
      </Field>
      {edit ? (
        <div className="mc-scr-out">
          <span className="mc-scr-out-h" aria-hidden="true">{SURFACE_LABELS.outcome}</span>
          <RecoTiles
            columns={4}
            label={SURFACE_LABELS.outcome}
            options={options}
            value={draft.value ?? null}
            onChange={(value) => change({ ...draft, value: value as ScreenOutcome, reason: undefined })}
          />
          <div className="mc-scr-foot">
            {recorded && status === "hold" ? <Stamp actor={recorded.actor} at={recorded.at} held /> : <span />}
            <Button tone="primary" blocked={recordBlocked} busy={recording} onClick={onRecordClick} hs={hs.record}>
              {ACTION_LABELS.recordOutcome}
            </Button>
          </div>
        </div>
      ) : decided ? (
        <div className="mc-scr-out">
          {recordedValues()}
          {recorded ? <div className="mc-scr-foot"><span />{<Stamp actor={recorded.actor} at={recorded.at} held={status === "hold"} />}</div> : null}
        </div>
      ) : null}
      {sync ? <Sync {...sync} className={cx("mc-scr-sync", sync.className)} /> : null}
      {children}
    </Card>
  );
}
